import { useEffect, useState } from "react";
import { MagnifyingGlass, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CCNav, PeriodBar, usePeriods, fmtAmt, MATCH_STATUS_STYLE, MATCH_STATUS_LABEL } from "../components/cc/common";
import { CCGuide } from "../components/cc/GuideBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const ORDER = ["conflict", "ambiguous", "candidate", "unmatched", "direct_assigned", "verified_deterministic", "manual_approved"];

function AssignSection({ match, note, busy, setBusy, onClose, onDecided }) {
  const [producers, setProducers] = useState([]);
  const [allocs, setAllocs] = useState([{ producer_id: "", percent: "" }]);
  useEffect(() => {
    setAllocs([{ producer_id: "", percent: "" }]);
    api.get("/commission/producers").then(({ data }) => setProducers(data));
  }, [match?.id]);

  const setAlloc = (i, field, v) => setAllocs(allocs.map((a, j) => (j === i ? { ...a, [field]: v } : a)));
  const splitMode = allocs.length > 1 || allocs.some((a) => a.percent !== "");

  const submit = async (clear = false) => {
    if (!clear) {
      if (allocs.some((a) => !a.producer_id)) { toast.error("Pick a producer for every line"); return; }
      if (note.trim().length < 5) { toast.error("A note (min 5 chars) is required for direct assignments"); return; }
    }
    setBusy(true);
    try {
      await api.put(`/commission/matching/${match.id}/assign`, {
        allocations: clear ? [] : allocs.map((a) => ({ producer_id: a.producer_id, percent: splitMode ? a.percent : null })),
        note,
      });
      toast.success(clear ? "Assignment cleared" : "Producer(s) assigned — this row will pay/charge back without a policy link");
      onClose(); onDecided();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assignment failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-fuchsia-200 bg-fuchsia-50/50 rounded p-3" data-testid="cc-assign-section">
      <p className="text-xs font-medium text-fuchsia-800 mb-1">Assign producer directly (no policy)</p>
      <p className="text-[11px] text-slate-500 mb-2">
        For cancelled/off-book policies still paying or charging back. One producer = their normal commission
        rules; add a second producer to split explicit % of the carrier amount instead.
      </p>
      {match.direct_assignment && (
        <div className="text-xs bg-white border border-fuchsia-200 rounded p-2 mb-2" data-testid="cc-assign-current">
          Currently assigned: {match.direct_assignment.allocations.map((a) =>
            `${a.producer_name}${a.percent ? ` ${a.percent}%` : " (rules)"}`).join(" / ")}
          <Button size="sm" variant="ghost" className="h-6 ml-2 text-red-600" disabled={busy}
            onClick={() => submit(true)} data-testid="cc-assign-clear-btn">Clear</Button>
        </div>
      )}
      {allocs.map((a, i) => (
        <div key={i} className="flex gap-1.5 mb-1.5 items-center">
          <select value={a.producer_id} onChange={(e) => setAlloc(i, "producer_id", e.target.value)}
            className="flex-1 h-8 rounded border border-slate-200 bg-white px-2 text-sm" data-testid={`cc-assign-producer-${i}`}>
            <option value="">Select producer…</option>
            {producers.filter((p) => p.status !== "former").map((p) => (
              <option key={p.id} value={p.id}>{p.name_raw}{p.user_name ? ` → ${p.user_name}` : " (unlinked)"}</option>
            ))}
          </select>
          {splitMode && (
            <Input value={a.percent} onChange={(e) => setAlloc(i, "percent", e.target.value)} placeholder="%"
              className="w-20 h-8" data-testid={`cc-assign-percent-${i}`} />
          )}
          {allocs.length > 1 && (
            <Button size="sm" variant="ghost" className="h-8 px-2 text-slate-400"
              onClick={() => setAllocs(allocs.filter((_, j) => j !== i))} data-testid={`cc-assign-remove-${i}`}>×</Button>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        {allocs.length < 4 && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy}
            onClick={() => setAllocs([...allocs, { producer_id: "", percent: "" }])} data-testid="cc-assign-add-btn">
            + Split with another producer
          </Button>
        )}
        <Button size="sm" className="h-7 text-xs bg-fuchsia-700 hover:bg-fuchsia-800" disabled={busy}
          onClick={() => submit(false)} data-testid="cc-assign-submit-btn">
          Assign producer{allocs.length > 1 ? "s" : ""}
        </Button>
      </div>
    </div>
  );
}

function DecideDialog({ match, onClose, onDecided }) {
  const [note, setNote] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(""); setQuery(""); setResults([]); }, [match?.id]);
  if (!match) return null;

  const searchPolicies = async () => {
    const { data } = await api.get(`/commission/policies?search=${encodeURIComponent(query)}`);
    setResults(data.policies.slice(0, 8));
  };

  const decide = async (action, policyId) => {
    if (note.trim().length < 5) {
      toast.error("A note (min 5 chars) is required for manual decisions");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/commission/matching/${match.id}/decide`, { action, policy_id: policyId || null, note });
      toast.success(action === "approve" ? "Match approved" : "Marked as no-match");
      onClose();
      onDecided();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!match} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="cc-decide-dialog">
        <DialogHeader><DialogTitle>Resolve match — {match.policy_number_raw || "no policy #"}</DialogTitle></DialogHeader>
        <div className="text-sm bg-slate-50 rounded p-3 grid grid-cols-2 gap-y-1">
          <span className="text-slate-500">Insured</span><span>{match.insured_name || "—"}</span>
          <span className="text-slate-500">Carrier / file</span><span>{match.carrier_name} · {match.batch_filename}</span>
          <span className="text-slate-500">Type / date</span><span>{match.transaction_type || "—"} · {match.transaction_date || "—"}</span>
          <span className="text-slate-500">Amount</span><span>{fmtAmt(match.amount)}</span>
        </div>
        {match.conflicts.length > 0 && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="cc-match-conflicts">
            {match.conflicts.map((c, i) => <p key={i}>{c}</p>)}
          </div>
        )}
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1.5">Candidates ({match.candidates.length})</p>
          {match.candidates.length === 0 && <p className="text-xs text-slate-400">No candidates found by the engine — search below or mark as no-match.</p>}
          <div className="space-y-2">
            {match.candidates.map((c) => (
              <div key={c.policy_id} className="border border-slate-200 rounded p-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-mono">{c.policy_number} <span className="font-sans text-slate-500">· {c.insured_name || "—"}</span></p>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {c.evidence.map((e, i) => (
                      <Badge key={i} className={e.type === "exact_id" ? "bg-emerald-100 text-emerald-800" : e.type === "prefix_id" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}>
                        {e.detail}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => decide("approve", c.policy_id)} data-testid={`cc-approve-candidate-${c.policy_id}`}>
                  <CheckCircle size={14} className="mr-1" /> Match
                </Button>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1.5">Search any policy</p>
          <div className="flex gap-1.5">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Policy # or insured name" data-testid="cc-policy-search-input"
              onKeyDown={(e) => e.key === "Enter" && query && searchPolicies()} />
            <Button variant="outline" onClick={searchPolicies} disabled={!query} data-testid="cc-policy-search-btn"><MagnifyingGlass size={15} /></Button>
          </div>
          {results.map((p) => (
            <div key={p.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
              <span className="font-mono">{p.policy_number} <span className="font-sans text-slate-500">· {p.insured_name || "—"}</span></span>
              <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={() => decide("approve", p.id)} data-testid={`cc-approve-search-${p.id}`}>Match</Button>
            </div>
          ))}
        </div>
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1">Decision note (required)</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why is this the right call? e.g. confirmed in EZLynx, carrier reissued number…" data-testid="cc-decision-note-input" />
        </div>
        <AssignSection match={match} note={note} busy={busy} setBusy={setBusy} onClose={onClose} onDecided={onDecided} />
        <Button variant="outline" className="text-slate-600" disabled={busy} onClick={() => decide("unmatch")} data-testid="cc-unmatch-btn">
          Mark as no-match
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function CommissionMatchingPage() {
  const { hasPerm } = useAuth();
  const { periods, selected, setSelected, reload } = usePeriods();
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (pk = selected) => {
    if (!pk) return;
    const [m, s] = await Promise.all([
      api.get(`/commission/matching?period_key=${pk}`),
      api.get(`/commission/matching/stats?period_key=${pk}`),
    ]);
    setMatches(m.data);
    setStats(s.data);
  };
  useEffect(() => { load(); }, [selected]);

  const totalCount = Object.values(stats).reduce((a, n) => a + n, 0);
  const shown = matches
    .filter((m) => !filter || m.status === filter)
    .sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status));

  const runMatching = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/commission/matching/run", { period_key: selected });
      toast.success(`Matching complete: ${data.verified_deterministic} verified, ${data.candidate + data.ambiguous + data.conflict} need review, ${data.unmatched} unmatched (${data.manual_kept} manual decisions kept)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Matching run failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="commission-matching-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-heading font-semibold">Policy matching</h1>
          <p className="text-sm text-slate-500 mt-1">Every statement transaction tied to a canonical policy — conflicts always stop for human review.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodBar periods={periods} selected={selected} setSelected={setSelected}
            onCreated={async (k) => { await reload(); setSelected(k); }} />
          {hasPerm("commission.resolve") && (
            <Button size="sm" disabled={busy || !selected} onClick={runMatching} data-testid="cc-run-matching-btn">
              <ArrowsClockwise size={15} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Run matching
            </Button>
          )}
        </div>
      </div>
      <CCNav />
      <CCGuide periodKey={selected} />

      <div className="flex gap-1.5 flex-wrap mb-4" data-testid="cc-match-stats">
        <button onClick={() => setFilter("")} className={`px-3 py-1 rounded-full text-xs font-medium ${!filter ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="cc-match-filter-all">
          All ({totalCount})
        </button>
        {ORDER.map((s) => (
          <button key={s} onClick={() => setFilter(filter === s ? "" : s)} data-testid={`cc-match-filter-${s}`}
            className={`px-3 py-1 rounded-full text-xs font-medium ${filter === s ? "ring-2 ring-indigo-400 " : ""}${MATCH_STATUS_STYLE[s]}`}>
            {MATCH_STATUS_LABEL[s]} ({stats[s] || 0})
          </button>
        ))}
      </div>

      {matches.length >= 5000 && (
        <p className="text-xs text-amber-700 mb-2" data-testid="cc-match-truncation-note">Showing the first 5,000 matches — use the state filters to narrow down; counts above cover everything.</p>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {shown.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">
            {matches.length === 0
              ? "No matches yet — matching runs automatically when statements validate, or hit Run matching."
              : "Nothing in this state."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cc-matches-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-2">Txn policy #</th>
                  <th className="px-4 py-2">Insured</th>
                  <th className="px-4 py-2">Carrier · file</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2 text-right">Amount</th>
                  <th className="px-4 py-2">State</th>
                  <th className="px-4 py-2">Matched to</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setActive(m)} data-testid={`cc-match-row-${m.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs">{m.policy_number_raw || "—"}</td>
                    <td className="px-4 py-2.5 max-w-[160px] truncate">{m.insured_name || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 max-w-[200px] truncate">{m.carrier_name} · {m.batch_filename}</td>
                    <td className="px-4 py-2.5 text-xs">{m.transaction_date || "—"}</td>
                    <td className="px-4 py-2.5 text-right">{fmtAmt(m.amount)}</td>
                    <td className="px-4 py-2.5"><Badge className={MATCH_STATUS_STYLE[m.status]}>{MATCH_STATUS_LABEL[m.status]}</Badge></td>
                    <td className="px-4 py-2.5 text-xs">
                      {m.policy_number_matched
                        ? <span className="font-mono">{m.policy_number_matched}</span>
                        : m.direct_assignment
                          ? <span className="text-fuchsia-700">{m.direct_assignment.allocations.map((a) => `${a.producer_name}${a.percent ? ` ${a.percent}%` : ""}`).join(" / ")}</span>
                          : <span className="text-slate-400">{m.candidates.length > 0 ? `${m.candidates.length} candidate${m.candidates.length > 1 ? "s" : ""}` : "—"}</span>}
                      {m.manual && m.decided_by_name && <span className="text-slate-400"> · {m.decided_by_name} {fmtDate(m.decided_at)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {hasPerm("commission.resolve")
        ? <DecideDialog match={active} onClose={() => setActive(null)} onDecided={load} />
        : null}
    </div>
  );
}
